/* eslint-disable complexity */
import { ProductType, Ribbon, Price } from '@/data/types/Product';
import valueIsString from 'integration/utils/typeAssertions/valueIsString';
import { formatPrice } from 'integration/utils/formatPrice';
import { useMemo } from 'react';

export const isDpOffer = (val: unknown) => {
	if (!valueIsString(val)) {
		return false;
	}

	return val.trim().toUpperCase().includes('DPS_OFERTA');
};

export const isDpPromotion = (val: unknown) => {
	if (!valueIsString(val)) {
		return false;
	}

	return val.trim().toUpperCase().includes('DPS_PROMOCION');
};

export const parseOffersDpOffer = (value: string): { offer: number; offerAdditional: number } => {
	const regex = /^(\d+)%?(?:,\+?(\d+)%)?$/;
	const match = value.replace(/\s+/g, '').match(regex);

	if (!match) {
		return { offer: 0, offerAdditional: 0 };
	}

	const offer = match[1] ? parseFloat(match[1]) / 100 : 0;
	const offerAdditional = match[2] ? parseFloat(match[2]) / 100 : 0;

	return { offer, offerAdditional };
};

export const extractNumberFromString = (input: string) => {
	const match = input.match(/\d+/);
	return match ? parseFloat(match[0]) / 100 : 0;
};

export const extractDpOffer = (ribbons: Ribbon[]) => {
	const dpOfferRibbons = ribbons.filter(
		(ribbon) => isDpOffer(ribbon.identifier) && ribbon.value.includes('%')
	);
	const dpPromotionRibbon = ribbons.find(
		(ribbon) => isDpPromotion(ribbon.identifier) && ribbon.value.includes('%')
	);

	if (dpPromotionRibbon && dpOfferRibbons.length === 0) {
		const offer = extractNumberFromString(dpPromotionRibbon.value);

		return {
			offer,
			offerAdditional: 0,
			ribbonOffer: dpPromotionRibbon,
			ribbonOfferAdditional: undefined,
		};
	}

	if (dpPromotionRibbon && dpOfferRibbons.length === 1) {
		const offer = extractNumberFromString(dpPromotionRibbon.value);
		const offerAdditional = extractNumberFromString(dpOfferRibbons[0].value);

		return {
			offer,
			offerAdditional,
			ribbonOffer: dpPromotionRibbon,
			ribbonOfferAdditional: dpOfferRibbons[0],
		};
	}

	if (
		dpOfferRibbons.length === 1 &&
		dpOfferRibbons[0].value.includes('+') &&
		dpOfferRibbons[0].value.split('%').filter(Boolean).length === 1
	) {
		const offerAdditional = extractNumberFromString(dpOfferRibbons[0].value);
		return {
			offer: 0,
			offerAdditional,
			ribbonOffer: undefined,
			ribbonOfferAdditional: dpOfferRibbons[0],
		};
	}

	if (dpOfferRibbons.length === 1) {
		const { offer, offerAdditional } = parseOffersDpOffer(dpOfferRibbons[0].value);
		return {
			offer,
			offerAdditional,
			ribbonOffer: offer > 0 ? dpOfferRibbons[0] : undefined,
			ribbonOfferAdditional: offerAdditional > 0 ? dpOfferRibbons[0] : undefined,
		};
	}

	const ribbonOffer = dpOfferRibbons.find((ribbon) => !ribbon.value.includes('+'));
	const ribbonOfferAdditional = dpOfferRibbons.find((ribbon) => ribbon.value.includes('+'));

	if (!ribbonOffer || !ribbonOfferAdditional) {
		return {
			offer: 0,
			offerAdditional: 0,
			ribbonOffer: undefined,
			ribbonOfferAdditional: undefined,
		};
	}

	const offer = extractNumberFromString(ribbonOffer.value);
	const offerAdditional = extractNumberFromString(ribbonOfferAdditional.value);

	return {
		offer,
		offerAdditional,
		ribbonOffer,
		ribbonOfferAdditional,
	};
};

interface InfoElectronico {
	offerDeContado?: string;
	strikedDeContado?: string;
}
interface Args {
	product: ProductType;
	infoElectronico?: InfoElectronico;
}

export const usePriceToCheckDpOffer = ({ infoElectronico, product }: Args) => {
	const { discount, price } = useMemo(() => {
		if (infoElectronico) {
			return {
				discount: infoElectronico.strikedDeContado
					? formatPrice(infoElectronico.offerDeContado || '', 'MXN')
					: null,
				price: formatPrice(
					infoElectronico.strikedDeContado || infoElectronico.offerDeContado || '',
					'MXN'
				),
			};
		}

		const rawPrice: Price = product.price.filter((item: Price) => item.usage === 'Display')[0];
		const rawDiscount: Price = product.price.filter((item: Price) => item.usage === 'Offer')[0];

		const listPrice = Number(rawPrice.value);
		const offerPrice = Number(rawDiscount.value);

		const { offer: offerDp, offerAdditional: offerDpAdditional } = extractDpOffer(product.ribbons);

		if (offerDp > 0 && offerDpAdditional > 0) {
			const firstOffer = listPrice - listPrice * offerDp;
			const finalOffer = firstOffer - firstOffer * offerDpAdditional;

			const discount =
				finalOffer !== listPrice ? formatPrice(finalOffer, rawDiscount.currency) : null;
			const price = formatPrice(rawPrice.value, rawPrice.currency);

			return { discount, price };
		}

		if (offerDpAdditional > 0) {
			const finalOffer = offerPrice - offerPrice * offerDpAdditional;

			const discount =
				finalOffer !== offerPrice ? formatPrice(finalOffer, rawDiscount.currency) : null;
			const price = formatPrice(rawPrice.value, rawPrice.currency);

			return { discount, price };
		}

		if (offerDp > 0) {
			const finalOffer = offerPrice - offerPrice * offerDp;

			const discount =
				finalOffer !== offerPrice ? formatPrice(finalOffer, rawDiscount.currency) : null;
			const price = formatPrice(rawPrice.value, rawPrice.currency);

			return { discount, price };
		}

		const discount =
			offerPrice > 0 && offerPrice !== listPrice
				? formatPrice(offerPrice, rawDiscount.currency)
				: null;
		const price = formatPrice(rawPrice.value, rawPrice.currency);

		return { discount, price };
	}, [product.price, infoElectronico, product.ribbons]);

	return { discount, price };
};
